import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import {
  assignmentsApi,
  organisationApi,
  type AssignmentDetail,
} from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  DateField,
  Dialog,
  ErrorState,
  LoadingState,
  Panel,
  ReadOnlyField,
  SelectField,
  StatusBadge,
  TextAreaField,
} from '../../components/ui/index.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { AssignmentDates } from './AssignmentDates.js';
import { ReadinessBanner } from './ReadinessBanner.js';
import {
  formatCalendarDate,
  testName,
  toneForAssignmentStatus,
  toneForAttemptStatus,
} from './status.js';

type Action = 'assignee' | 'department' | 'delay' | 'stop' | 'cancel' | 'retest' | null;

const OPEN = ['not_started', 'in_progress', 'temporarily_stopped'];

/**
 * One assignment: what the work is, who holds it, its dates, its attempts and
 * its whole history.
 *
 * EVERY ACTION HERE IS RE-CHECKED BY THE BACKEND. The buttons follow the
 * caller's permissions and the work's state so people are not offered what
 * they cannot do, but hiding a button protects nothing (`CLAUDE.md` section 5).
 *
 * REASSIGNING NEVER MOVES ENTERED WORK. The dialog says so before it is used:
 * an open draft by the previous person is closed as discontinued, with their
 * name on it, and the new person starts an attempt of their own.
 */
export function AssignmentFilePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locale = currentLocale();

  const [action, setAction] = useState<Action>(null);
  const [reason, setReason] = useState('');
  const [personId, setPersonId] = useState<string | null>(null);
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [expectedDate, setExpectedDate] = useState<string | null>(null);

  const assignment = useQuery({
    queryKey: ['assignment', id],
    queryFn: () => assignmentsApi.get(id as string),
  });
  const departments = useQuery({
    queryKey: ['departments', 'all'],
    queryFn: () => organisationApi.listDepartments(true),
  });
  const mayReassign = can(PERMISSIONS.ASSIGNMENTS_REASSIGN);
  const assignees = useQuery({
    queryKey: ['assignees'],
    queryFn: () => assignmentsApi.assignees(),
    enabled: mayReassign,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['assignment', id] });
    await queryClient.invalidateQueries({ queryKey: ['assignments'] });
    await queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const openAction = (next: Action) => {
    setReason('');
    setPersonId(null);
    setDepartmentId(null);
    setExpectedDate(null);
    setAction(next);
  };

  const perform = useMutation({
    mutationFn: async () => {
      const target = id as string;
      const withReason = reason.trim() ? { reason: reason.trim() } : {};
      switch (action) {
        case 'assignee':
          return assignmentsApi.setAssignee(target, { assignedTo: personId, ...withReason });
        case 'department':
          return assignmentsApi.transferDepartment(target, {
            departmentId,
            ...(reason.trim() ? { note: reason.trim() } : {}),
          });
        case 'delay':
          return assignmentsApi.recordDelay(target, { reason: reason.trim(), expectedDate });
        case 'stop':
          return assignmentsApi.stop(target, {
            reason: reason.trim(),
            ...(expectedDate ? { expectedDate } : {}),
          });
        case 'cancel':
          return assignmentsApi.cancel(target, { reason: reason.trim() });
        case 'retest': {
          const attempt = await assignmentsApi.startAttempt(target, { retestReason: reason.trim() });
          navigate(`/attempts/${attempt.id}`);
          return null;
        }
        default:
          return null;
      }
    },
    onSuccess: async () => {
      await refresh();
      setAction(null);
    },
  });

  const start = useMutation({
    mutationFn: () => assignmentsApi.startAttempt(id as string),
    onSuccess: (attempt) => navigate(`/attempts/${attempt.id}`),
  });
  const resume = useMutation({
    mutationFn: () => assignmentsApi.resume(id as string),
    onSuccess: refresh,
  });

  // D235: "Assign" on a sample's Lab Work arrives with `?assign=1` and the
  // assign dialog already open. The sample and the test are this work's own;
  // the person picks only who does it. The flag is consumed once.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantsAssign = searchParams.get('assign') === '1';
  const loaded = assignment.data;
  useEffect(() => {
    if (!wantsAssign || !loaded) return;
    if (mayReassign && OPEN.includes(loaded.status) && loaded.assignedTo === null) {
      setReason('');
      setPersonId(null);
      setAction('assignee');
    }
    setSearchParams(
      (params) => {
        params.delete('assign');
        return params;
      },
      { replace: true },
    );
  }, [wantsAssign, loaded, mayReassign, setSearchParams]);

  if (assignment.isLoading) return <LoadingState label={t('common.loading')} />;
  if (assignment.error) {
    return <ErrorState error={assignment.error} onRetry={() => void assignment.refetch()} />;
  }
  const record: AssignmentDetail | undefined = assignment.data;
  if (!record) return null;

  const departmentName = (departmentKey: string | null) => {
    if (!departmentKey) return null;
    const found = (departments.data?.data ?? []).find((item) => item.id === departmentKey);
    if (!found) return null;
    return found.name || found.code;
  };

  const isOpen = OPEN.includes(record.status);
  const isMine = record.assignedTo?.id === user?.id;
  const mayFollowUp = mayReassign || (can(PERMISSIONS.RESULTS_ENTER) && isMine);
  const mayStart =
    isMine &&
    can(PERMISSIONS.RESULTS_ENTER) &&
    record.openAttemptId === null &&
    (record.status === 'not_started' || record.status === 'in_progress');
  const mayRetest = isMine && can(PERMISSIONS.RESULTS_ENTER) && record.status === 'awaiting_review';
  const reasonRequired =
    action === 'delay' ||
    action === 'stop' ||
    action === 'cancel' ||
    action === 'retest' ||
    (action === 'assignee' && record.assignedTo !== null);
  const canConfirm =
    (!reasonRequired || reason.trim().length >= 3) &&
    (action !== 'assignee' || personId !== null) &&
    (action !== 'department' || departmentId !== null) &&
    (action !== 'delay' || expectedDate !== null);

  const followUp = isOpen ? (
    <div className="flex flex-wrap gap-inline">
      {mayReassign ? (
        <ActionButton priority="tertiary" size="sm" onPress={() => openAction('assignee')}>
          {record.assignedTo ? t('work.reassign') : t('work.assign')}
        </ActionButton>
      ) : null}
      {mayReassign ? (
        <ActionButton priority="tertiary" size="sm" onPress={() => openAction('department')}>
          {t('work.transferDepartment')}
        </ActionButton>
      ) : null}
      {mayFollowUp ? (
        <ActionButton priority="tertiary" size="sm" onPress={() => openAction('delay')}>
          {t('work.recordDelay')}
        </ActionButton>
      ) : null}
      {mayFollowUp && record.status !== 'temporarily_stopped' ? (
        <ActionButton priority="tertiary" size="sm" onPress={() => openAction('stop')}>
          {t('work.stop')}
        </ActionButton>
      ) : null}
      {mayFollowUp && record.status === 'temporarily_stopped' ? (
        <ActionButton
          priority="tertiary"
          size="sm"
          isLoading={resume.isPending}
          onPress={() => resume.mutate()}
        >
          {t('work.resume')}
        </ActionButton>
      ) : null}
      {can(PERMISSIONS.ASSIGNMENTS_CREATE) ? (
        <ActionButton priority="tertiary" size="sm" onPress={() => openAction('cancel')}>
          {t('work.cancelWork')}
        </ActionButton>
      ) : null}
    </div>
  ) : null;

  return (
    <>
      {/*
        THE RECORD BAR (D278): the record's number and state, its title and
        context, and the next step at the trailing end.
      */}
      <section className="mb-gutter rounded-large border border-default-200 bg-white px-4 py-3">
        <div className="flex flex-wrap items-start gap-stack">
          <div className="min-w-0 grow basis-60">
            <div className="flex flex-wrap items-center gap-inline">
              <span className="measure text-body font-semibold text-default-900" dir="ltr">
                {record.sample.sampleNumber}
                {record.pieceCode ? ` / ${record.pieceCode}` : ''}
              </span>
              <StatusBadge
                label={t(`status.test.${record.status}`)}
                tone={toneForAssignmentStatus(record.status)}
              />
              {record.isDelayed ? <StatusBadge label={t('work.delayedBadge')} tone="warning" /> : null}
            </div>
            <h1 className="mt-1 text-title font-bold text-default-900">{testName(record.test, locale)}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-inline">
            <ActionButton priority="tertiary" onPress={() => navigate('/work')}>
              {t('common.back')}
            </ActionButton>
            {record.openAttemptId && can(PERMISSIONS.RESULTS_VIEW) ? (
              <ActionButton priority="secondary" onPress={() => navigate(`/attempts/${record.openAttemptId}`)}>
                {isMine ? t('myTasks.continueDraft') : t('work.viewDraft')}
              </ActionButton>
            ) : null}
            {mayStart ? (
              <ActionButton priority="primary" isLoading={start.isPending} onPress={() => start.mutate()}>
                {t('work.startAttempt')}
              </ActionButton>
            ) : null}
            {mayRetest ? (
              <ActionButton priority="secondary" onPress={() => openAction('retest')}>
                {t('work.startRetest')}
              </ActionButton>
            ) : null}
          </div>
        </div>
      </section>

      {start.error ? <ErrorNotice error={start.error} /> : null}

      <div className="flex flex-col gap-gutter">
        <ReadinessBanner readiness={record.test.templateReadiness} />

        <div className="grid grid-cols-1 items-start gap-gutter lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="flex min-w-0 flex-col gap-gutter">
            <Panel title={t('work.detailsTitle')} actions={followUp}>
              <div className="grid grid-cols-1 gap-gutter sm:grid-cols-2">
                <ReadOnlyField label={t('assign.referenceFact')}>
                  {record.specificationText ?? t('common.notRecorded')}
                </ReadOnlyField>
                <ReadOnlyField label={t('assign.instructionsLabel')}>
                  {record.instructions ?? t('common.notRecorded')}
                </ReadOnlyField>
                {record.duplicateOfAssignmentId ? (
                  <ReadOnlyField label={t('work.duplicateOf')}>
                    <Link to={`/work/${record.duplicateOfAssignmentId}`} className="hover:underline">
                      {record.duplicateReason}
                    </Link>
                  </ReadOnlyField>
                ) : null}
                {record.latestStop ? (
                  <ReadOnlyField label={t('work.stoppedBecause')}>{record.latestStop.reason}</ReadOnlyField>
                ) : null}
                {record.cancellationReason ? (
                  <ReadOnlyField label={t('work.cancelledBecause')}>{record.cancellationReason}</ReadOnlyField>
                ) : null}
              </div>
            </Panel>

            <Panel title={t('work.attemptsTitle')} description={t('work.attemptsHint')}>
              {record.attempts.length === 0 ? (
                <p className="text-body text-default-600">{t('work.noAttempts')}</p>
              ) : (
                <ol className="flex flex-col divide-y divide-default-100">
                  {record.attempts.map((attempt) => (
                    <li key={attempt.id} className="flex flex-wrap items-start gap-stack py-2.5 first:pt-0 last:pb-0">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-inline">
                          <p className="text-body font-medium text-default-900">
                            {t('attempt.numbered', { number: attempt.attemptNumber })}
                          </p>
                          <StatusBadge
                            label={t(`attemptStatus.${attempt.status}`)}
                            tone={toneForAttemptStatus(attempt.status)}
                            size="sm"
                          />
                        </div>
                        <p className="mt-1 text-caption text-default-600">
                          {t('attempt.performedBy', { name: attempt.performedBy.displayName ?? '' })}
                        </p>
                        {attempt.retestReason ? (
                          <p className="text-caption text-default-600">
                            {t('attempt.retestBecause', { reason: attempt.retestReason })}
                          </p>
                        ) : null}
                        {attempt.discontinuedReason ? (
                          <p className="text-caption text-default-600">
                            {t('attempt.discontinuedBecause', { reason: attempt.discontinuedReason })}
                          </p>
                        ) : null}
                      </div>
                      {can(PERMISSIONS.RESULTS_VIEW) ? (
                        <ActionButton
                          priority="tertiary"
                          size="sm"
                          className="ms-auto"
                          onPress={() => navigate(`/attempts/${attempt.id}`)}
                        >
                          {t('common.open')}
                        </ActionButton>
                      ) : null}
                    </li>
                  ))}
                </ol>
              )}
            </Panel>
          </div>

          <div className="flex min-w-0 flex-col gap-gutter">
            <Panel title={t('work.factsTitle')}>
              <div className="flex flex-col gap-stack">
                <ReadOnlyField label={t('samples.sampleId')}>
                  <Link to={`/samples/${record.sample.id}`} className="measure hover:underline" dir="ltr">
                    {record.sample.sampleNumber}
                  </Link>
                </ReadOnlyField>
                <ReadOnlyField label={t('work.assigneeColumn')}>
                  {record.assignedTo?.displayName ?? t('work.unassigned')}
                </ReadOnlyField>
                <ReadOnlyField label={t('assign.departmentLabel')}>
                  {departmentName(record.departmentId) ?? t('common.notRecorded')}
                </ReadOnlyField>
                <ReadOnlyField label={t('work.priorityColumn')}>
                  {t(`workOrderPriority.${record.priority}`)}
                </ReadOnlyField>
                <ReadOnlyField label={t('work.datesColumn')}>
                  <AssignmentDates assignment={record} />
                </ReadOnlyField>
              </div>
            </Panel>

            <Panel title={t('work.historyTitle')} description={t('work.historyHint')}>
              <ol className="flex flex-col gap-stack">
                {record.events.map((event) => (
                  <li key={event.id} className="relative min-w-0 ps-4">
                    <span
                      aria-hidden="true"
                      className="absolute start-0 top-1.5 size-2 rounded-full border border-default-500 bg-white"
                    />
                    <p className="text-body font-medium text-default-900">
                      {t(`assignmentEvent.${event.eventType}`)}
                    </p>
                    {/* Name and date isolated, so a Latin name cannot split an
                        Arabic-formatted date beside it. */}
                    <p className="text-caption text-default-600">
                      <bdi>{event.recordedBy.displayName ?? t('common.notRecorded')}</bdi>
                      {' · '}
                      <bdi>{formatDateTime(event.recordedAt, { locale })}</bdi>
                    </p>
                    {event.toUser ? (
                      <p className="text-caption text-default-600">
                        {event.fromUser
                          ? t('work.movedFromTo', { from: event.fromUser.displayName ?? '', to: event.toUser.displayName ?? '' })
                          : t('work.givenTo', { name: event.toUser.displayName ?? '' })}
                      </p>
                    ) : null}
                    {event.toDepartmentId && event.eventType === 'department_transferred' ? (
                      <p className="text-caption text-default-600">
                        {t('work.transferredTo', { name: departmentName(event.toDepartmentId) ?? '' })}
                      </p>
                    ) : null}
                    {event.newExpectedDate ? (
                      <p className="text-caption text-default-600">
                        {t('work.expectedChanged', {
                          from: formatCalendarDate(event.previousExpectedDate, locale) ?? t('work.noDueDate'),
                          to: formatCalendarDate(event.newExpectedDate, locale),
                        })}
                      </p>
                    ) : null}
                    {event.reason ? <p className="text-body text-default-800">{event.reason}</p> : null}
                    {event.note ? <p className="text-caption text-default-600">{event.note}</p> : null}
                  </li>
                ))}
              </ol>
              <p className="mt-stack text-caption text-default-600">{t('work.historyAppendOnly')}</p>
            </Panel>
          </div>
        </div>
      </div>

      <Dialog
        isOpen={action !== null}
        onClose={() => setAction(null)}
        title={action ? t(`work.dialog.${action}`) : ''}
        footer={
          <ActionBar>
            <ActionButton priority="tertiary" onPress={() => setAction(null)} isDisabled={perform.isPending}>
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={!canConfirm}
              isLoading={perform.isPending}
              onPress={() => perform.mutate()}
            >
              {t('common.confirm')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          {action === 'assignee' ? (
            <>
              <Alert tone="info" title={t('work.reassignNoticeTitle')} description={t('work.reassignNoticeBody')} />
              <SelectField
                label={t('assign.assigneeLabel')}
                value={personId}
                onChange={setPersonId}
                isRequired
                options={(assignees.data ?? [])
                  .filter((person) => person.id !== record.assignedTo?.id)
                  .map((person) => ({ value: person.id, label: person.displayName }))}
              />
            </>
          ) : null}
          {action === 'department' ? (
            <SelectField
              label={t('assign.departmentLabel')}
              value={departmentId}
              onChange={setDepartmentId}
              isRequired
              options={(departments.data?.data ?? [])
                .filter((item) => item.isActive && item.id !== record.departmentId)
                .map((item) => ({
                  value: item.id,
                  label: item.name || item.code || '',
                }))}
            />
          ) : null}
          {action === 'retest' ? (
            <Alert tone="info" title={t('work.retestNoticeTitle')} description={t('work.retestNoticeBody')} />
          ) : null}
          {action === 'cancel' ? (
            <Alert tone="warning" title={t('work.cancelNoticeTitle')} description={t('work.cancelNoticeBody')} />
          ) : null}
          {action === 'delay' || action === 'stop' ? (
            <DateField
              label={t('work.expectedDateLabel')}
              description={
                action === 'delay'
                  ? t('work.expectedDateDelayHint', {
                      date: formatCalendarDate(record.dueDate, locale) ?? t('work.noDueDate'),
                    })
                  : t('common.optional')
              }
              value={expectedDate}
              onChange={setExpectedDate}
              isRequired={action === 'delay'}
            />
          ) : null}
          {action ? (
            <TextAreaField
              label={action === 'department' ? t('work.noteLabel') : t('common.reason')}
              description={reasonRequired ? t('common.required') : t('common.optional')}
              value={reason}
              onChange={setReason}
              isRequired={reasonRequired}
              rows={3}
            />
          ) : null}
          {perform.error ? <ErrorNotice error={perform.error} /> : null}
        </div>
      </Dialog>
    </>
  );
}
